
import { doc, getDoc, serverTimestamp, setDoc } from 'firebase/firestore';
import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';
import { firestore } from '../../firebase/firebase';
import Button from '../common/Button';
import FormInput from '../common/Input';
import FormSelect from '../common/FormSelect';



const DURATION_OPTIONS = [
    { value: "3", label: "3 Months" },
    { value: "6", label: "6 Months" },
    { value: "9", label: "9 Months" },
    { value: "12", label: "12 Months" },
    { value: "lifetime", label: "Lifetime" }
];
const ReferralRewardsManager = () => {
    const { currentUser, isAdmin } = useAuth();
    const [settings, setSettings] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [hasChanges, setHasChanges] = useState(false);

    useEffect(() => {
        const loadSettings = async () => {
            try {
                const settingsDoc = await getDoc(doc(firestore, "referralRewards", "settings"));
                setSettings(settingsDoc.data());
            } catch (error) {
                console.error("Error loading settings:", error);
                toast.error("Failed to load referral settings");
            } finally {
                setLoading(false);
            }
        };

        loadSettings();
    }, []);

    const handleUpdateSettings = async () => {
        if (!isAdmin) return;

        try {
            setSaving(true);
            await setDoc(doc(firestore, "referralRewards", "settings"), {
                ...settings,
                lastUpdated: serverTimestamp(),
                updatedBy: currentUser.uid
            });
            toast.success("Settings updated successfully");
            setHasChanges(false);
        } catch (error) {
            console.error("Error updating settings:", error);
            toast.error("Failed to update settings");
        } finally {
            setSaving(false);
        }
    };

    const handleSettingsChange = (newSettings) => {
        setSettings(newSettings);
        setHasChanges(true);
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center min-h-[200px]">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-primary"></div>
            </div>
        );
    }

    return (
        <div className="bg-white rounded-lg shadow overflow-hidden mt-8">
            <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                    <h2 className="text-2xl font-bold text-gray-900">Referral Rewards Settings</h2>
                    {hasChanges && (
                        <Button
                            variant="primary"
                            onClick={handleUpdateSettings}
                            loading={saving}
                            disabled={!hasChanges}
                        >
                            Save Changes
                        </Button>
                    )}
                </div>

                <div className="space-y-6">
                    {/* Initial Credits Setting */}
                    <div className="bg-gray-50 p-4 rounded-lg">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">New User Credits</h3>
                        <div className="max-w-xs">
                            <FormInput
                                label="Initial Credits"
                                type="number"
                                value={settings?.initialCredits || 0}
                                onChange={(e) => {
                                    handleSettingsChange({
                                        ...settings,
                                        initialCredits: parseInt(e.target.value)
                                    });
                                }}
                                min="0"
                                helpText="Credits awarded to new users upon signup"
                            />
                        </div>
                    </div>

                    {/* Referral Rewards */}
                    <div className="bg-gray-50 p-4 rounded-lg">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Referral Rewards Tiers</h3>
                        <div className="space-y-4">
                            {settings?.rewards.map((reward, index) => (
                                <div key={index} className="bg-white p-4 rounded-lg border border-gray-200">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <FormInput
                                            label="Required Referrals"
                                            type="number"
                                            value={reward.requiredReferrals}
                                            onChange={(e) => {
                                                const newRewards = [...settings.rewards];
                                                newRewards[index] = {
                                                    ...reward,
                                                    requiredReferrals: parseInt(e.target.value)
                                                };
                                                handleSettingsChange({
                                                    ...settings,
                                                    rewards: newRewards
                                                });
                                            }}
                                            min="1"
                                        />

                                        <div className="flex gap-4 items-start">
                                            <div className="flex-1">
                                                <FormSelect
                                                    label="Duration"
                                                    value={String(reward.duration)}
                                                    onChange={(e) => {
                                                        const newRewards = [...settings.rewards];
                                                        newRewards[index] = {
                                                            ...reward,
                                                            duration: e.target.value,
                                                            durationUnit: "months"
                                                        };
                                                        handleSettingsChange({
                                                            ...settings,
                                                            rewards: newRewards
                                                        });
                                                    }}
                                                >
                                                    {DURATION_OPTIONS.map(option => (
                                                        <option key={option.value} value={option.value}>
                                                            {option.label}
                                                        </option>
                                                    ))}
                                                </FormSelect>
                                            </div>

                                            <Button
                                                variant="danger"
                                                size="sm"
                                                onClick={() => {
                                                    const newRewards = settings.rewards.filter((_, i) => i !== index);
                                                    handleSettingsChange({
                                                        ...settings,
                                                        rewards: newRewards
                                                    });
                                                }}
                                                className="mt-7"
                                            >
                                                Remove
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            ))}

                            <Button
                                variant="outline"
                                fullWidth
                                onClick={() => {
                                    const newRewards = [...settings.rewards, {
                                        requiredReferrals: 5,
                                        rewardType: "unlimited",
                                        duration: "3",
                                        durationUnit: "months"
                                    }];
                                    handleSettingsChange({
                                        ...settings,
                                        rewards: newRewards
                                    });
                                }}
                            >
                                Add New Reward Tier
                            </Button>
                        </div>
                    </div>

                    {/* Last Updated Info */}
                    {settings?.lastUpdated && (
                        <div className="text-sm text-gray-500 mt-4">
                            Last updated: {new Date(settings.lastUpdated.toDate()).toLocaleString()}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};


export default ReferralRewardsManager;